import React, { useCallback, useEffect, useState } from "react";
import {
    alpha,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import User from "../../../api/User";
import Booking, { BookingState } from "../../../api/Booking";
import Service from "../../../api/Service";
import TextField from "../../TextField";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { parseWithOffset } from "../../../utils/dayjsUtils";
import UserProfileHeader from "../Users/UserProfileHeader";
import dayjs, { Dayjs } from "dayjs";
import DateTimePicker from "../../Modals/DateTimePicker";
import { FullScreenLoader } from "../../FullScreenLoader";
import FormattedTime from "../../FormattedTime";
import FormattedPrice from "../../FormattedPrice";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

export const loader = async (request: any) => {
    const booking_id = await request.params.booking_id;
    return { booking_id };
};

const BookingInfo = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const hex = theme.palette.primary.main;

    const booking_id = (useLoaderData() as any).booking_id as string;
    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [booking, setBooking] = useState<Booking | undefined>();
    const [service, setService] = useState<Service | undefined>();
    const [user, setUser] = useState<User | undefined>();
    const [contactInfo, setContactInfo] = useState<{
        email?: string;
        phone?: string;
    }>({});

    const [confirmed, setConfirmed] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);
    const [needsReview, setNeedsReview] = useState(false);

    const [note, setNote] = useState("");

    const inThePast = () => {
        if (!booking || !booking.start) return false;
        return parseWithOffset(booking.end).isBefore(dayjs());
    };

    const fetchBooking = useCallback(async () => {
        if (!uuid || !brand_id || !location_id || !booking_id) return;

        try {
            const fetchedBooking = await Booking.info(
                uuid,
                brand_id,
                location_id,
                booking_id
            );
            if (fetchedBooking) {
                setBooking(fetchedBooking);
                setConfirmed(fetchedBooking.confirmed);
                setIsCanceled(fetchedBooking.state === BookingState.Cancel);
                setNeedsReview(
                    !fetchedBooking.confirmed &&
                        fetchedBooking.state !== BookingState.Cancel &&
                        fetchedBooking.miaCandidate === true
                );
                if (fetchedBooking.note) setNote(fetchedBooking.note);
                Service.info(uuid, brand_id, fetchedBooking.service)
                    .then(setService)
                    .catch(console.error);
                User.info(uuid, brand_id, fetchedBooking.user)
                    .then((user) => {
                        setUser(user);
                        setContactInfo({
                            email: user?.email,
                            phone: user?.phone,
                        });
                    })
                    .catch(console.error);
            }
        } catch (err) {
            console.error(err);
            navigate("/calendar");
        }
    }, [uuid, brand_id, location_id, booking_id, navigate]);

    useEffect(() => {
        fetchBooking();
    }, [fetchBooking]);

    const onBack = useCallback(() => {
        if (window.history.length > 2) {
            navigate(-1);
        } else {
            navigate("/notifications");
        }
    }, [navigate]);

    const onCancel = useCallback(() => {
        if (!uuid || !brand_id || !location_id || !booking) return;

        Booking.updateState(
            uuid,
            brand_id,
            location_id,
            booking.key,
            BookingState.Cancel
        )
            .then(() => {
                setIsCanceled(true);
                setNeedsReview(false);
            })
            .catch(console.error);
    }, [uuid, brand_id, location_id, booking]);

    const onConfirm = useCallback(() => {
        if (!uuid || !brand_id || !location_id || !booking || confirmed) return;

        setConfirmed(true);
        setNeedsReview(false);
        Booking.updateConfirmed(
            uuid,
            brand_id,
            location_id,
            booking.key,
            true
        ).catch((err) => {
            console.error(err);
            setConfirmed(false);
        });
    }, [uuid, brand_id, location_id, booking, confirmed]);

    const onNoteSave = useCallback(() => {
        if (!uuid || !brand_id || !location_id || !booking) return;

        Booking.updateNote(
            uuid,
            brand_id,
            location_id,
            booking.key,
            note
        ).catch(console.error);
    }, [uuid, brand_id, location_id, booking, note]);

    const handleReschedule = useCallback(
        async (dateTime: Dayjs, close: () => void) => {
            if (!uuid || !brand_id || !location_id || !booking || !service)
                return;

            const endDate = dateTime.add(service.duration, "minute").toDate();

            try {
                await Booking.update(
                    uuid,
                    brand_id,
                    location_id,
                    booking.key,
                    dateTime.toDate(),
                    endDate
                );
                fetchBooking();
                close();
            } catch (error) {
                console.error("Error rescheduling:", error);
            }
        },
        [uuid, brand_id, location_id, booking, service, fetchBooking]
    );

    if (!booking || !user || !service) return <FullScreenLoader />;

    return (
        <Stack
            direction="column"
            width="100%"
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
        >
            {/* Compact hero */}
            <Stack
                width="100%"
                sx={{
                    background: `linear-gradient(180deg, ${lighten(hex, 0.08)} 0%, ${darken(hex, 0.08)} 100%)`,
                    pt: "16px",
                    pb: 5,
                    px: 2,
                    boxSizing: "border-box",
                    position: "relative",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            >
                {/* Back + title row */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={2}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.18)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 1,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <ButtonBase onClick={onBack} sx={{ borderRadius: "50%" }}>
                        <ColoredIcon icon={BackIcon} color="white" size="32px" />
                    </ButtonBase>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="18px"
                        color="white"
                    >
                        Appointment
                    </Typography>
                </Stack>

                {/* User profile header area */}
                <UserProfileHeader user={user} />
            </Stack>

            {/* Bottom sheet */}
            <Stack
                sx={{
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    position: "relative",
                    zIndex: 1,
                    pt: 3,
                    px: 2,
                    pb: "40px",
                    boxSizing: "border-box",
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
                spacing={2.5}
            >
                {/* === SERVICE CARD === */}
                <Stack
                    sx={{ ...CARD_STYLES }}
                    spacing={1.5}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={600}
                            fontSize="16px"
                            color="text.primary"
                        >
                            {service.name}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <FormattedPrice
                            price={booking.price}
                            currency={service.currency}
                            discount={booking.discount}
                        />
                        <FormattedTime time={booking.duration} />
                    </Stack>
                    {/* Action chips */}
                    {!inThePast() && (
                        <Stack direction="row" spacing={1} pt={0.5}>
                            <DateTimePicker
                                duration={service.duration}
                                button={{
                                    variant: "small",
                                    sx: {
                                        bgcolor: alpha(hex, 0.08),
                                        borderRadius: "20px",
                                        px: 1.5,
                                        py: 0.5,
                                        minWidth: "auto",
                                    },
                                    textColor: hex,
                                }}
                                onSave={handleReschedule}
                            >
                                {booking.start ? "Reschedule" : "Schedule"}
                            </DateTimePicker>
                            {booking.start && !isCanceled && (
                                <ButtonBase
                                    onClick={onCancel}
                                    sx={{
                                        borderRadius: 20,
                                        px: 1.5,
                                        py: 0.5,
                                        bgcolor: alpha(
                                            theme.palette.error.main,
                                            0.06
                                        ),
                                    }}
                                >
                                    <Typography
                                        fontFamily={FONTS.display}
                                        fontSize="13px"
                                        fontWeight={600}
                                        color={theme.palette.error.main}
                                    >
                                        Cancel
                                    </Typography>
                                </ButtonBase>
                            )}
                        </Stack>
                    )}
                </Stack>

                {/* === SCHEDULED TIME & DATE === */}
                {booking.start && (
                    <Stack sx={{ ...CARD_STYLES }} spacing={1}>
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}
                        >
                            Scheduled
                        </Typography>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            <Stack
                                direction="row"
                                alignItems="baseline"
                                spacing={0.75}
                            >
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontWeight={700}
                                    fontSize="28px"
                                    color="text.primary"
                                >
                                    {parseWithOffset(booking.start).get("date")}
                                </Typography>
                                <Stack spacing={-0.25}>
                                    <Typography
                                        fontFamily={FONTS.display}
                                        fontWeight={600}
                                        fontSize="14px"
                                        color="text.secondary"
                                    >
                                        {parseWithOffset(booking.start).format(
                                            "MMMM"
                                        )}
                                    </Typography>
                                    <Typography
                                        fontSize="12px"
                                        color="text.secondary"
                                    >
                                        {parseWithOffset(booking.start).format(
                                            "dddd"
                                        )}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontWeight={600}
                                    fontSize="16px"
                                    color={hex}
                                >
                                    {parseWithOffset(booking.start).format("HH:mm")}
                                </Typography>
                                <Typography
                                    fontWeight={600}
                                    fontSize="16px"
                                    color={hex}
                                >
                                    —
                                </Typography>
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontWeight={600}
                                    fontSize="16px"
                                    color={hex}
                                >
                                    {parseWithOffset(booking.end).format("HH:mm")}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                )}

                {/* === NEEDS REVIEW WARNING === */}
                {!inThePast() && needsReview && (
                    <Stack
                        direction="column"
                        alignItems="center"
                        spacing={2}
                        sx={{
                            ...CARD_STYLES,
                            bgcolor: alpha(theme.palette.warning.main, 0.06),
                            border: `1.5px solid ${alpha(
                                theme.palette.warning.main,
                                0.2
                            )}`,
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={600}
                            color={darken(theme.palette.warning.main, 0.3)}
                            fontSize="15px"
                            textAlign="center"
                        >
                            Action needed: Client hasn't confirmed this booking
                        </Typography>
                        {contactInfo.email && (
                            <Typography color="text.secondary" fontSize="14px">
                                Email: {contactInfo.email}
                            </Typography>
                        )}
                        {contactInfo.phone && (
                            <Typography color="text.secondary" fontSize="14px">
                                Phone: {contactInfo.phone}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1} mt={1}>
                            <ButtonBase
                                onClick={onConfirm}
                                sx={{
                                    borderRadius: 20,
                                    px: 2,
                                    py: 0.75,
                                    bgcolor: alpha(hex, 0.1),
                                }}
                            >
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontSize="13px"
                                    fontWeight={600}
                                    color={hex}
                                >
                                    Confirm
                                </Typography>
                            </ButtonBase>
                            <ButtonBase
                                onClick={onCancel}
                                sx={{
                                    borderRadius: 20,
                                    px: 2,
                                    py: 0.75,
                                    bgcolor: alpha(
                                        theme.palette.error.main,
                                        0.06
                                    ),
                                }}
                            >
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontSize="13px"
                                    fontWeight={600}
                                    color={theme.palette.error.main}
                                >
                                    Cancel
                                </Typography>
                            </ButtonBase>
                        </Stack>
                    </Stack>
                )}

                {/* === CONFIRM BUTTON === */}
                {!inThePast() && !isCanceled && !needsReview && (
                    <ButtonBase
                        onClick={onConfirm}
                        disabled={confirmed}
                        sx={{
                            borderRadius: 20,
                            px: 3,
                            py: 1,
                            alignSelf: "flex-start",
                            bgcolor: confirmed
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(hex, 0.1),
                            opacity: confirmed ? 0.7 : 1,
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="14px"
                            fontWeight={600}
                            color={
                                confirmed
                                    ? theme.palette.success.main
                                    : hex
                            }
                        >
                            {confirmed ? "Confirmed" : "Confirm"}
                        </Typography>
                    </ButtonBase>
                )}

                {/* === CANCELED NOTICE === */}
                {isCanceled && (
                    <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            ...CARD_STYLES,
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                            py: 2.5,
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={700}
                            fontSize="18px"
                            color={theme.palette.error.main}
                            textAlign="center"
                        >
                            Canceled
                        </Typography>
                    </Stack>
                )}

                {/* === NOTE === */}
                <Stack sx={{ ...CARD_STYLES }} spacing={1}>
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        }}
                    >
                        Note
                    </Typography>
                    <TextField
                        value={note}
                        onChange={(e) => setNote(e.currentTarget.value)}
                        placeholder="Note..."
                        fullWidth
                        multiline
                        variant="contained"
                        sx={{
                            bgcolor: "transparent",
                            padding: 1,
                            fontWeight: 400,
                            fontSize: "15px",
                            lineHeight: "24.9px",
                        }}
                    />
                    <ButtonBase
                        onClick={onNoteSave}
                        sx={{
                            borderRadius: 20,
                            px: 2,
                            py: 0.75,
                            bgcolor: alpha(hex, 0.08),
                            alignSelf: "flex-start",
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="13px"
                            fontWeight={600}
                            color={hex}
                        >
                            Save note
                        </Typography>
                    </ButtonBase>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default BookingInfo;
